import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { Box, Card, CardContent, Container, Grid, Stack, Typography } from '@mui/material';
// components
import { FeatureWidget, ContactWidget, AchieversWidget } from '../../sections/web/home';
import { CourseHero, CourseCurriculum, CourseDetailCard, MoreCourseWidget } from '../../sections/web/course';
import HeaderBreadcrumbs from '../../components/header-breadcrumbs';
import { Widget404, LoadSkeleton } from '../../components/widegts';
import Markdown from '../../components/markdown';
import { Seo, breadcrumbSchema, courseSchema, truncateText } from '../../components/seo';
import { MotionInView, varFadeInUp } from '../../components/animate';
import Iconify from '../../components/iconify';

// redux
import { useDispatch, useSelector } from '../../redux/store';

// data
import { getCourse } from '../../redux/slices/course';

import {

  LINK_DOWNLOADS,
} from '../../data/contact-info';
// ----------------------------------------------------------------------


// Both widgets live in the quarter-width sidebar and render 2-up, so the caps
// are set to fill complete rows.
const ACHIEVERS_LIMIT = 6;
const MORE_COURSES_LIMIT = 4;

const revealTransition = { duration: 0.5, ease: [0.43, 0.13, 0.23, 0.96] };

// ----------------------------------------------------------------------

// Matches the sidebar card recipe used by CourseDetailCard so the column
// reads as one stack rather than three unrelated panels.
const SidebarCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: `0 10px 30px ${alpha(theme.palette.grey[900], 0.06)}`,
}));

const SidebarCardAccent = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: 3,
  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0)})`,
}));

function AppDownloadCard() {
  return (
    <SidebarCard>
      <SidebarCardAccent />

      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
          <Iconify icon="eva:smartphone-outline" width={20} sx={{ color: 'primary.main' }} />
          <Typography component="h2" variant="subtitle2" sx={{ color: 'text.primary' }}>
            Study on the go
          </Typography>
        </Stack>

        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 2.5 }}>
          Watch lessons, download material and track your progress from the ASAP UAE app.
        </Typography>

        {/* The store badges carry their own wording ("Get it on Google Play"),
            so they are shown at full size rather than shrunk into an icon —
            at icon size the badge text is unreadable. The fixed-height wrapper
            reserves the space so the card does not shift as the images load. */}
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', useFlexGap: true }}>
          {LINK_DOWNLOADS.map((link) => (
            <Box
              key={link.name}
              component="a"
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                flex: '1 1 150px',
                maxWidth: 200,
                height: { xs: 52, sm: 60 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0.75,
                borderRadius: (theme) => theme.shape.borderRadius,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                transition: 'border-color 0.3s ease, background-color 0.3s ease, transform 0.3s ease',
                '@media (hover: hover)': {
                  '&:hover': {
                    borderColor: (theme) => alpha(theme.palette.primary.main, 0.45),
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                    transform: 'translateY(-2px)',
                  },
                },
              }}
            >
              <Box
                component="img"
                src={link.logo}
                alt={`Get the ASAP UAE app on ${link.name === 'IOS' ? 'the App Store' : 'Google Play'}`}
                loading="lazy"
                decoding="async"
                sx={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain' }}
              />
            </Box>
          ))}
        </Stack>

        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'text.disabled', mt: 2, lineHeight: 1.6 }}
        >
          Free to download. Sign in with your ASAP UAE account to pick up where you left off.
        </Typography>
      </CardContent>
    </SidebarCard>
  );
}

// ----------------------------------------------------------------------

export default function CourseDetailPage() {
  const { courseslug } = useParams();
  const dispatch = useDispatch();
  const { course, error, isLoading } = useSelector((state) => state.course);

  useEffect(() => {
    dispatch(getCourse(courseslug));
  }, [dispatch, courseslug]);

  if (!course && isLoading) {
    return <LoadSkeleton />;
  }
  if (!course && error) {
    return <Widget404 item="Course" subitem={`No data found as ${courseslug} `} />;
  }
  if (!course) {
    return <LoadSkeleton />;
  }

  const pageTitle = course.name;

  // Structured data mirrors the rendered page: the course entity plus the
  // visible breadcrumb trail (Home > this course).
  const jsonLd = [
    courseSchema({
      name: pageTitle,
      description: course.introduction,
      image: course.image,
      path: `/course/${courseslug}`,
    }),
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: pageTitle, path: `/course/${courseslug}` },
    ]),
  ];

  return (
    <>
      <Seo
        title={pageTitle}
        description={truncateText(
          `${course.introduction ? `${course.introduction} ` : ''}Prepare for ${pageTitle} with ASAP UAE — lessons, practice questions and mentor support.`
        )}
        path={`/course/${courseslug}`}
        jsonLd={jsonLd}
      />

      <Container maxWidth="xl" sx={{ pb: { xs: 6, md: 8 } }}>
        {/* The page title is the hero h1, so the breadcrumb stays a div. */}
        <HeaderBreadcrumbs
          heading={pageTitle}
          headingComponent="div"
          links={[{ name: pageTitle }]}
          sx={{ mb: { xs: 3, md: 4 } }}
        />

        <CourseHero course={course} />

        {/* Overview: long-form content beside a quarter-width sidebar. The
            sidebar carries the at-a-glance counts, the app download prompt, and
            the compact achiever and related-course strips. */}
        <Grid container spacing={{ xs: 4, md: 6 }} sx={{ pt: { xs: 5, md: 7 } }}>
          <Grid size={{ xs: 12, md: 8 }}>
            <MotionInView variants={varFadeInUp} transition={revealTransition}>
              {course.introduction && (
                <Typography
                  component="p"
                  variant="h6"
                  sx={{
                    mb: { xs: 3, md: 4 },
                    fontSize: { xs: 18, sm: 20 },
                    lineHeight: 1.6,
                    fontWeight: 500,
                    color: 'text.primary',
                  }}
                >
                  {course.introduction}
                </Typography>
              )}

              {course.about ? <Markdown>{course.about}</Markdown> : null}
            </MotionInView>

            <Box component="section" sx={{ pt: { xs: 5, md: 7 } }}>
              <CourseCurriculum
                subjects={course.subjects}
                isLoading={isLoading}
                eyebrow="Syllabus"
                title="Course Curriculum"
                description="Every module covered in this programme, in the order you will study it."
              />
            </Box>

            <Box component="section" sx={{ pt: { xs: 5, md: 7 } }}>
              <FeatureWidget />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={{ xs: 4, md: 5 }}>
              <MotionInView variants={varFadeInUp} transition={revealTransition}>
                <CourseDetailCard course={course} />
              </MotionInView>

              <MotionInView variants={varFadeInUp} transition={revealTransition}>
                <AppDownloadCard />
              </MotionInView>

              <MotionInView variants={varFadeInUp} transition={revealTransition}>
                <AchieversWidget
                  course={courseslug}
                  variant="sidebar"
                  limit={ACHIEVERS_LIMIT}
                  eyebrow="Recent passes"
                  title="Achievers"
                  description="Learners who completed this programme."
                />
              </MotionInView>

              <MotionInView variants={varFadeInUp} transition={revealTransition}>
                <MoreCourseWidget currentcourse={courseslug} limit={MORE_COURSES_LIMIT} />
              </MotionInView>
            </Stack>
          </Grid>
        </Grid>

        <Box component="section" sx={{ pt: { xs: 6, md: 8 } }}>
          <ContactWidget />
        </Box>
      </Container>
    </>
  );
}
